import type { DigitalGoodsService } from './digitalGoods';

/**
 * Google Play Billing dans la TWA (A112) : enveloppe de la **Digital Goods API**
 * (prix, achats existants) et de la **Payment Request API** (l'achat lui-même).
 * Ces deux API n'existent que dans Chrome lancé par l'application Android
 * installée depuis Google Play (la TWA) ; sur le web, sur iOS et dans Chrome
 * hors TWA, `getPlayBilling()` répond `null` et l'écran d'achat propose un
 * lien vers la fiche Play et la saisie d'un code.
 *
 * **Aucune exception ne sort de ce module** : chaque échec se traduit en
 * `null` / `false` / `{ ok: false }`. L'état « acheté » n'est pas décidé ici
 * (`entitlement/index.ts`). Le jeton est vérifié et **acquitté** par le Worker (`ack.ts`, A118) :
 * la Digital Goods API 2.0 n'a plus d'`acknowledge()` et la TWA n'acquitte pas.
 */

/** Produit intégré unique et non consommable (Play Console → Produits intégrés). */
export const PLAY_SKU = 'diceordie_full';
/** Méthode de paiement de la Payment Request API pour Google Play Billing. */
export const PLAY_METHOD = 'https://play.google.com/billing';
/** Fiche du jeu dans le Play Store (lien de l'écran d'achat hors TWA). */
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.nanocorp.diceordie';

export type PurchaseOutcome =
  | { ok: true; token: string }
  | { ok: false; reason: 'cancelled' | 'error' };

export interface PlayBilling {
  /** Prix localisé du jeu complet (« 2,35 € »), `null` si Play ne répond pas. */
  price(): Promise<string | null>;
  /** Le produit figure-t-il dans les achats de l'utilisateur ? `false` aussi quand Play ne répond pas. */
  owned(): Promise<boolean>;
  /**
   * Comme `owned()`, mais `null` quand Play ne répond pas : « pas de réponse » ne doit jamais passer
   * pour « non acheté » (un remboursement n'est constaté que sur un `false` clair, `index.ts`).
   */
  ownedKnown(): Promise<boolean | null>;
  /**
   * Les jetons d'achat du produit dans `listPurchases()` (A118) : liste vide si Play répond « rien »,
   * `null` si Play ne répond pas. Ils sont transmis au Worker pour être acquittés.
   */
  ownedTokens(): Promise<string[] | null>;
  /** Lance l'achat (fenêtre Play) puis l'acquitte. */
  purchase(): Promise<PurchaseOutcome>;
}

let cached: Promise<PlayBilling | null> | null = null;

/** Oublie le résultat gardé (tests). */
export function resetPlayBillingCache(): void {
  cached = null;
}

/** Formate un prix de Play (`{ currency, value }`) dans la langue de l'appareil ; `null` si illisible. */
export function formatPlayPrice(price: { currency?: unknown; value?: unknown } | undefined, locale?: string): string | null {
  if (!price) return null;
  const value = Number(price.value);
  const currency = typeof price.currency === 'string' ? price.currency : '';
  if (!Number.isFinite(value) || !/^[A-Za-z]{3}$/.test(currency)) return null;
  try {
    return new Intl.NumberFormat(locale ?? (typeof navigator !== 'undefined' ? navigator.language : undefined), {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(value);
  } catch {
    return null;
  }
}

function isAbort(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { name?: unknown }).name === 'AbortError';
}

function wrap(service: DigitalGoodsService): PlayBilling {
  /** Les achats du produit, `null` quand Play ne répond pas (ou répond autre chose qu'une liste). */
  const listOwned = async (): Promise<{ count: number; tokens: string[] } | null> => {
    try {
      const purchases = await service.listPurchases();
      if (!Array.isArray(purchases)) return null;
      const mine = purchases.filter((p) => p && p.itemId === PLAY_SKU);
      const tokens = mine.map((p) => p.purchaseToken).filter((t): t is string => typeof t === 'string' && t.length > 0);
      return { count: mine.length, tokens };
    } catch {
      return null;
    }
  };
  const ownedKnown = async (): Promise<boolean | null> => {
    const owned = await listOwned();
    return owned ? owned.count > 0 : null;
  };
  return {
    async price(): Promise<string | null> {
      try {
        const details = await service.getDetails([PLAY_SKU]);
        const item = Array.isArray(details) ? details.find((d) => d && d.itemId === PLAY_SKU) ?? details[0] : undefined;
        return formatPlayPrice(item?.price);
      } catch {
        return null;
      }
    },

    ownedKnown,

    async ownedTokens(): Promise<string[] | null> {
      return (await listOwned())?.tokens ?? null;
    },

    async owned(): Promise<boolean> {
      return (await ownedKnown()) === true;
    },

    async purchase(): Promise<PurchaseOutcome> {
      try {
        if (typeof PaymentRequest === 'undefined') return { ok: false, reason: 'error' };
        // Le total est exigé par la Payment Request API et ignoré par Play (le prix vient du produit).
        const request = new PaymentRequest(
          [{ supportedMethods: PLAY_METHOD, data: { sku: PLAY_SKU } }],
          { total: { label: 'DiceOrDie', amount: { currency: 'EUR', value: '0' } } },
        );
        const response = await request.show();
        const details = response.details as { purchaseToken?: unknown } | null | undefined;
        const token = typeof details?.purchaseToken === 'string' ? details.purchaseToken : '';
        if (!token) {
          try {
            await response.complete('fail');
          } catch {
            /* rien à rattraper */
          }
          return { ok: false, reason: 'error' };
        }
        // `complete('success')` ne suffit pas à acquitter (A118 : c'est le Worker qui le fait, `ack.ts`) ;
        // `acknowledge`, quand il existe encore, est appelé en plus (sans conséquence s'il est déjà acquitté).
        try {
          await response.complete('success');
        } catch {
          /* l'achat a eu lieu : on ne le perd pas pour un acquittement refusé */
        }
        try {
          if (typeof service.acknowledge === 'function') await service.acknowledge(token, 'onetime');
        } catch {
          /* idem */
        }
        return { ok: true, token };
      } catch (err) {
        return { ok: false, reason: isAbort(err) ? 'cancelled' : 'error' };
      }
    },
  };
}

/**
 * Le service Play de cet appareil, ou `null` (web, iOS, Chrome hors TWA, ou
 * appel refusé). Résultat gardé pour la session.
 */
export function getPlayBilling(): Promise<PlayBilling | null> {
  if (cached) return cached;
  cached = (async (): Promise<PlayBilling | null> => {
    try {
      if (typeof window === 'undefined' || typeof window.getDigitalGoodsService !== 'function') return null;
      const service = await window.getDigitalGoodsService(PLAY_METHOD);
      return service ? wrap(service) : null;
    } catch {
      return null;
    }
  })();
  return cached;
}
